import { cloneElement } from "react";

import { Flex, Box } from "@chakra-ui/react";
import dayjs from "dayjs";
import { useIntl } from "react-intl";

import { useGetHubStockChangeLogsQuery } from "flows/Inventory/shared/queries/inventoryEntry/inventoryEntry.generated";
import { useGetEmployeesNameByBadgeNoQuery } from "shared/queries/employee/employee.generated";
import { Spinner } from "ui/Spinner/Spinner";
import { BodyS, TitleM, HeaderM, DetailL } from "ui/Typography/Typography";

import { reasonMetadata } from "../utils/constants";

type StockChangeLogsProps = {
  sku: string;
};

type LogsListProps = {
  logs: {
    reason: string | null;
    triggeredAt: string;
    quantity: number;
    actor: string;
  }[];
};

function LogsList({ logs }: LogsListProps) {
  const intl = useIntl();

  const { data: actors } = useGetEmployeesNameByBadgeNoQuery({
    variables: {
      input: {
        badgeNumbers: logs.map((log) => log.actor) || [],
      },
    },
  });

  const actorNamesByKey =
    actors?.getEmployees.reduce(
      (acc, employee) => {
        if (employee.badgeNo && employee.displayName) {
          acc[employee.badgeNo] = employee.displayName;
        }
        return acc;
      },
      {} as Record<string, string>,
    ) || {};

  return (
    <Flex gap="s200" direction="column">
      {logs.length === 0 ? (
        <Box p="s200">
          <DetailL textAlign="center">
            {intl.formatMessage({
              id: "components.inventory.stock_change_logs.no_logs",
            })}
          </DetailL>
        </Box>
      ) : (
        logs.map(({ reason, triggeredAt, quantity, actor }) => {
          const reasonMeta = reason ? reasonMetadata[reason] : null;
          return (
            <Flex gap="s50" flexDirection="column" key={triggeredAt}>
              <Flex alignItems="center" gap="s100">
                <HeaderM color={quantity > 0 ? "green.500" : "red.500"}>
                  {quantity > 0 ? `+${quantity}` : quantity}
                </HeaderM>
                {reasonMeta && (
                  <>
                    {reasonMeta?.icon && cloneElement(reasonMeta?.icon, { boxSize: "6" })}
                    <BodyS fontWeight={700}>{intl.formatMessage({ id: reasonMeta?.label })}</BodyS>
                  </>
                )}
              </Flex>
              <BodyS>{`${dayjs(triggeredAt).format("DD.MM.YYYY - HH:mm")} - ${
                actorNamesByKey[actor] || actor
              }`}</BodyS>
            </Flex>
          );
        })
      )}
    </Flex>
  );
}

export function StockChangeLogs({ sku }: StockChangeLogsProps) {
  const intl = useIntl();

  const { data, loading } = useGetHubStockChangeLogsQuery({
    fetchPolicy: "network-only",
    variables: { input: { sku } },
  });

  return (
    <Box borderRadius="sm" bg="white" w="100%" px="s300" py="s200">
      <TitleM color="grey.800" mb="s200">
        {intl.formatMessage({
          id: "components.inventory.stock_change_logs.title",
        })}
      </TitleM>
      {loading ? (
        <Flex justifyContent="center" alignItems="center" height="100px">
          <Spinner />
        </Flex>
      ) : (
        <LogsList logs={data?.getHubStockChangeLogs.stockChangeLogs || []} />
      )}
    </Box>
  );
}
